/**
 * WORLD-CUP-FIRST-72-HOURS-WARROOM-P0
 *
 * Lightweight in-browser per-day counter for any EventType we care about.
 * Subscribes to the global `vs:event` bus (fired by trackEvent) and
 * accumulates counts in localStorage. Read by the war-room dashboard.
 *
 * No backend. No realtime. No new tables. Per-device only — this is a
 * monitoring helper, not the source of truth.
 */
import type { EventType } from "@/types";

const KEY = "vs:warroom:counters";
const FIRST_PRED_KEY = "vs:warroom:firstPredBuckets";
const META_KEY = "vs:warroom:meta";

type Counters = Record<string, Record<string, number>>; // day → eventType → n

function load<T>(k: string, fallback: T): T {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(k);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}
function save(k: string, v: unknown) {
  try { window.localStorage.setItem(k, JSON.stringify(v)); } catch { /* noop */ }
}

export function ymd(t: number = Date.now()): string {
  const d = new Date(t);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function bump(event_type: string, by = 1, day = ymd()) {
  const all = load<Counters>(KEY, {});
  const today = all[day] ?? {};
  today[event_type] = (today[event_type] ?? 0) + by;
  all[day] = today;
  save(KEY, all);
}

export function getCounter(event_type: EventType | string, day = ymd()): number {
  const all = load<Counters>(KEY, {});
  return all[day]?.[event_type] ?? 0;
}

export function getAllForDay(day = ymd()): Record<string, number> {
  const all = load<Counters>(KEY, {});
  return all[day] ?? {};
}

export function getDailySeries(event_type: EventType | string, days = 3): { day: string; n: number }[] {
  const all = load<Counters>(KEY, {});
  const out: { day: string; n: number }[] = [];
  const now = Date.now();
  for (let i = days - 1; i >= 0; i--) {
    const d = ymd(now - i * 86_400_000);
    out.push({ day: d, n: all[d]?.[event_type] ?? 0 });
  }
  return out;
}

export type FirstPredBucket = "0-10s" | "10-30s" | "30-60s" | "60-120s" | "120s+";
const BUCKETS: FirstPredBucket[] = ["0-10s", "10-30s", "30-60s", "60-120s", "120s+"];

export function bumpFirstPredictionBucket(bucket: FirstPredBucket) {
  const all = load<Record<FirstPredBucket, number>>(FIRST_PRED_KEY, {
    "0-10s": 0, "10-30s": 0, "30-60s": 0, "60-120s": 0, "120s+": 0,
  });
  all[bucket] = (all[bucket] ?? 0) + 1;
  save(FIRST_PRED_KEY, all);
}
export function getFirstPredictionBuckets(): Record<FirstPredBucket, number> {
  return load<Record<FirstPredBucket, number>>(FIRST_PRED_KEY, {
    "0-10s": 0, "10-30s": 0, "30-60s": 0, "60-120s": 0, "120s+": 0,
  });
}

export interface DropOffMeta {
  routeVisits: Record<string, number>;
  lastRoute: string | null;
  lastRouteAt: number;
}

function loadMeta(): DropOffMeta {
  return load<DropOffMeta>(META_KEY, { routeVisits: {}, lastRoute: null, lastRouteAt: Date.now() });
}
function saveMeta(m: DropOffMeta) { save(META_KEY, m); }

export function recordRouteVisit(route: string) {
  const m = loadMeta();
  m.routeVisits[route] = (m.routeVisits[route] ?? 0) + 1;
  m.lastRoute = route;
  m.lastRouteAt = Date.now();
  saveMeta(m);
}
export function getRouteVisits(): DropOffMeta { return loadMeta(); }

const COUNTED_EVENTS = new Set<string>([
  "activation_started",
  "nation_selected",
  "first_prediction_completed",
  "prediction_vote",
  "first_share_completed",
  "first_referral_sent",
  "returned_same_day",
  "viral_share_completed",
  "share_link_generated",
  "share_success_feedback_shown",
  "global_share_clicked",
  "daily_wow_card_action",
  "daily_wow_shared",
  "challenge_friend_clicked",
  "challenge_friend_sent",
  "challenge_friend_accepted",
  "challenge_friend_status_changed",
  "clip_of_day_shown",
  "clip_of_day_opened",
  "nation_rank_badge_clicked",
  "ambassador_discovery_clicked",
  "creator_discovery_clicked",
  "victory_card_prompt_action",
  "audio_sync_started",
  "audio_sync_completed",
  "audio_sync_card_shown",
  "audio_sync_card_cta_clicked",
  "challenge_invite_received",
  "challenge_invite_accepted",
  "challenge_invite_dismissed",
  "challenge_result_shared",
  "day2_return_panel_shown",
  "day2_return_panel_tile_clicked",
  "notification_trigger_prepared",
  "first_reward_seen",
]);

let booted = false;
export function bootstrapWarRoomCounters(): void {
  if (typeof window === "undefined" || booted) return;
  booted = true;
  window.addEventListener("vs:event", (e: Event) => {
    const detail = (e as CustomEvent<{ event_type: string; event_data: Record<string, unknown> }>).detail;
    if (!detail) return;
    if (!COUNTED_EVENTS.has(detail.event_type)) return;
    bump(detail.event_type);
    if (detail.event_type === "first_prediction_completed") {
      const bucket = (detail.event_data?.bucket as FirstPredBucket) ?? "120s+";
      if (BUCKETS.includes(bucket)) bumpFirstPredictionBucket(bucket);
    }
  });
}
